import { z } from 'zod/v3';
import { CocoEvent } from '@cotera/client/app/etc';
import {
  ActionResult,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
  ActionTrackMetadata,
} from '@cotera/client/v0/actions/framework';
import type { ActionTrackingContext } from '@cotera/client/v0/actions/framework/action-tracking';
import { err, ok } from 'neverthrow';
import { CocoChatModel } from '../models/coco-chat.model';
import type { TenantedClient } from '@cotera/client/app/etc/api/typed-client';

const removeResourceArtifactInputSchema = z.object({
  artifactId: z.string().min(1),
});

type Params = z.infer<typeof removeResourceArtifactInputSchema>;

/**
 * Dismisses an artifact from the chat's artifact panel — the inverse of
 * {@link AddResourceArtifactAction}. The `remove_artifact` action suppresses the
 * artifact wherever it came from (a resource the user opened, a tool output, a
 * parent chat), so closing a tab also takes it out of Coco's active context.
 */
export class RemoveResourceArtifactAction extends BaseAction<
  Params,
  { t: string; message?: string }[]
> {
  title = 'Close Artifact';
  inputSchema = removeResourceArtifactInputSchema;
  group = 'Chat';
  description = 'Remove an artifact from the chat artifact panel';

  constructor(private readonly client: TenantedClient['resource']['chat']) {
    super();
  }

  applicable(context: ApplicableContext): boolean {
    return context.hasTargetedModelOfType(CocoChatModel);
  }

  async execute(
    params: Params,
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    const model = context.targetedModelOfType(CocoChatModel);
    if (model === null) {
      return err([{ t: 'error', message: 'Coco chat model not found' }]);
    }

    const result = await this.client.addAction({
      conversationId: model.conversationId,
      action: {
        type: 'remove_artifact',
        artifactId: params.artifactId,
      },
    });

    if (result.isErr()) {
      const errorMessage =
        result.error.t === 'application'
          ? result.error.err.message
          : 'Failed to remove artifact';
      return err([{ t: 'error', message: errorMessage }]);
    }

    return ok({});
  }

  track({
    payload,
    context,
  }: ActionTrackingContext<
    Params,
    { t: string; message?: string }[]
  >): ActionTrackMetadata | null {
    const model = context.targetedModelOfType(CocoChatModel);
    if (model === null) {
      return null;
    }

    return {
      event: CocoEvent.ResourceArtifactRemoved,
      properties: {
        conversationId: model.conversationId,
        artifactId: payload.artifactId,
      },
    };
  }
}
